<script>
import _ from 'lodash';

export default {
  name: 'DirectionsRenderer',
  inject: ['$mapPromise'],
  props: {
    origin: {
      type: Object,
      required: true,
    },
    destination: {
      type: Object,
      required: true,
    },
    directionsServiceRequestOptions: {
      type: Object,
      default: () => ({}),
    },
    directionsRendererOptions: {
      type: Object,
      default: () => ({
        suppressMarkers: true,
        preserveViewport: true,
      }),
    },
  },
  data() {
    return {
      componentMounted: false,
      map: null,
      route: null,
    };
  },
  computed: {
    originHasData() {
      return !_.isNil(this.$props.origin)
        && !_.isNil(this.$props.origin.lat)
        && _.isNumber(this.$props.origin.lat)
        && this.$props.origin.lat !== 0
        && !_.isNil(this.$props.origin.lng)
        && _.isNumber(this.$props.origin.lng)
        && this.$props.origin.lng !== 0;
    },

    destinationHasData() {
      return !_.isNil(this.$props.destination)
        && !_.isNil(this.$props.destination.lat)
        && _.isNumber(this.$props.destination.lat)
        && this.$props.destination.lat !== 0
        && !_.isNil(this.$props.destination.lng)
        && _.isNumber(this.$props.destination.lng)
        && this.$props.destination.lng !== 0;
    },
  },

  watch: {
    origin: {
      handler(val, old) {
        if (!this.$data.componentMounted) {
          return;
        }
        this.whenOriginOrDestinationChanged(val, old);
      },
      deep: true,
    },
    destination: {
      handler(val, old) {
        if (!this.$data.componentMounted) {
          return;
        }
        this.whenOriginOrDestinationChanged(val, old);
      },
      deep: true,
    },
    directionsServiceRequestOptions: {
      handler(val, old) {
        if (!this.$data.componentMounted) {
          return;
        }
        if (!_.isEqual(val, old)) {
          this.checkComponentInstallation();
          this.draw();
        }
      },
      deep: true,
    },
    directionsRendererOptions: {
      handler(val, old) {
        if (!this.$data.componentMounted) {
          return;
        }
        if (!_.isEqual(val, old)) {
          this.checkComponentInstallation();
          this.draw();
        }
      },
      deep: true,
    },
  },
  beforeMount() {
    // If someone of these component attributes does not could be because:
    //
    // 1. Developer does not use the component inside of <GmapMap> component
    // from 'vue2-google-maps' package
    //
    // 2. Developer has not installed the package in Vue
    // like this: Vue.use(VueGoogleMaps, {})
    this.checkComponentInstallation();
  },
  async mounted() {
    await this.draw();
    this.$data.componentMounted = true;
  },
  methods: {
    checkComponentInstallation() {
      if (!(this.$gmapApiPromiseLazy && this.$mapPromise)) {
        throw Error('Use DirectionsRenderer inside GmapMap component from \'vue2-google-maps\' package');
      }
    },
    callback(google) {
      // NOTE: travelMode property is mandatory by Google service
      const request = {
        travelMode: google.maps.TravelMode.WALKING,
        ...this.$props.directionsServiceRequestOptions,
        origin: {
          lat: this.$props.origin.lat,
          lng: this.$props.origin.lng,
        },
        destination: {
          lat: this.$props.destination.lat,
          lng: this.$props.destination.lng,
        },
      };
      this.$data.route = new google.maps.DirectionsRenderer({
        suppressMarkers: true,
        preserveViewport: true,
        ...this.$props.directionsRendererOptions,
        map: this.$data.map,
      });
      const directionsService = new google.maps.DirectionsService();
      directionsService.route(request, (response, status) => {
        if (status === google.maps.DirectionsStatus.OK) {
          // Display the route on the map.
          this.$data.route.setDirections(response);
          this.$emit('route-response', response);
        }
      });
    },
    remove() {
      if (this.$data.route) {
        this.$data.route.setMap(null);
        this.$data.route = null;
      }
    },
    draw() {
      return this.$gmapApiPromiseLazy().then(async (google) => {
        // I don't know if it's possible,
        // but I check it just in case
        this.remove();

        if (this.originHasData && this.destinationHasData) {
          this.$data.map = await this.$mapPromise;
          this.callback(google);
        }
      });
    },
    whenOriginOrDestinationChanged(val, old) {
      if (_.isNil(val)) {
        // When current origin or destination value is Nil
        // we have to remove the route
        this.remove();
        return;
      }
      if (!_.isNil(val) && _.isNil(old)) {
        // When current origin or destination value is not Nil and
        // old value was Nil, we have to draw the route.
        // In draw method, we will check if the other property exists
        this.checkComponentInstallation();
        this.draw();
        return;
      }
      // And what's happen when current value and old value
      // are not Nil? we have to check his props to know
      // if we have to repaint the route or not.
      const latIsDiff = val.lat !== old.lat;
      const lngIsDiff = val.lng !== old.lng;
      if (latIsDiff || lngIsDiff) {
        this.checkComponentInstallation();
        this.draw();
      }
    },
  },
  render: h => h(),
};

</script>
